import React, { useCallback, useState } from 'react';
import { AppButton } from 'uikit/buttons';
import { ReactComponent as Delete } from 'assets/img/common/delete.svg';
import { ConfirmResult, useConfirmationModal } from '../../components/dialogModal/useConfirmationModal';
import { FeatureFlags } from '../../application/constants/feature-flags';
import { QueryFactory } from '../../services/api';
import { useStudy } from '../../helpers/hooks/useStudy';
import { logger } from '../../application/logging/logging';
import { showServerErrorToast } from '../../components/toast/toast-helper';
import { useQueryClient } from '@tanstack/react-query';
import { useScopedTranslation } from '../../application/localisation/useScopedTranslation';
import { useGetPatientStudyCountersQuery } from '../../services/api/api-client/PatientQuery';

export const PatientsDelete = () => {
  const { t } = useScopedTranslation('StudySettingsPage.PatientsDelete', 'dev');
  const confirmationModal = useConfirmationModal({});
  const study = useStudy();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { data: counters } = useGetPatientStudyCountersQuery(study!.id, { enabled: !!study });

  const onDeleteHandler = useCallback(async () => {
    const result = await confirmationModal.open({
      title: t('Modal.Title'),
      text: t('Modal.Text'),
      okButtonText: t('Modal.ConfirmButtonText'),
      okButtonColorSchema: 'destroy',
    });

    if (result !== ConfirmResult.Confirm || !study) return;

    try {
      setIsLoading(true);
      await QueryFactory.TestClient.deletePatients(study.id);
      await Promise.all([
        queryClient.invalidateQueries(QueryFactory.PatientQuery.getPatientsQueryKey()),
        queryClient.invalidateQueries(QueryFactory.PatientQuery.getPatientStudyCountersQueryKey(study.id)),
      ]);
    } catch (e) {
      logger().error('Patient deletion is failed', e);

      showServerErrorToast(e);
    } finally {
      setIsLoading(false);
    }
  }, [confirmationModal.open, study?.id]);

  if (!FeatureFlags.isPatientDeletionEnabled() || counters?.length === 0) {
    return <></>;
  }

  return (
    <div style={{ display: 'flex' }}>
      <AppButton
        colorSchema={'destroy'}
        variant={'icon-link'}
        iconFirst={true}
        Icon={Delete}
        disabled={isLoading}
        text={t('Button')}
        onClick={onDeleteHandler}
      />

      {confirmationModal.modal}
    </div>
  );
};
